import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldWarningIcon as ShieldWarning, TrashIcon as Trash, GitMergeIcon as GitMerge } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Universal admin "danger zone" for any record page (dog, breeder,
 * litter, event). Renders nothing for non-admins. Shows:
 *
 * - Cascade-delete button (asks the user to type the record name to
 *   confirm). Calls DELETE /api/admin/records/{type}/{id}.
 * - Optional merge dialog (only for `recordType="dog"`). Calls
 *   POST /api/admin/records/dogs/merge with a canonical-id input.
 *
 * Props:
 *   recordType: "dog" | "breeder" | "litter" | "event"
 *   recordId:   string
 *   recordName: string  — shown in the confirm prompt
 *   onDeleted:  () => void — called after a successful delete (parent
 *               typically routes the user away)
 *   onMerged:   (canonicalId: string) => void  (dogs only)
 */
export default function AdminTools({
  recordType,
  recordId,
  recordName,
  onDeleted,
  onMerged,
}) {
  const { user } = useAuth();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [canonicalId, setCanonicalId] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user || user.role !== "admin") return null;

  // Normalise both sides of the confirmation check so trailing spaces
  // in the stored dog name or a stray Caps-Lock don't lock the admin
  // out of their own delete button. We still require a meaningful
  // (non-empty) match — admins can't bypass by typing nothing.
  const normalisedTarget = (recordName || "").trim().toLowerCase();
  const normalisedInput = confirmText.trim().toLowerCase();
  const confirmMatches = normalisedTarget.length > 0 && normalisedInput === normalisedTarget;

  const onDelete = async () => {
    if (!confirmMatches) {
      toast.error(`Type the ${recordType} name exactly to confirm.`);
      return;
    }
    setBusy(true);
    try {
      const r = await api.delete(`/admin/records/${recordType}/${recordId}`);
      const refs = r.data?.cascade
        ? Object.values(r.data.cascade).reduce((a, n) => a + (Number(n) || 0), 0)
        : 0;
      toast.success(
        refs > 0
          ? `Deleted. Cleaned ${refs} related record${refs === 1 ? "" : "s"}.`
          : "Deleted.",
      );
      setDeleteOpen(false);
      setConfirmText("");
      onDeleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    } finally {
      setBusy(false);
    }
  };

  const onMerge = async () => {
    const canonical = canonicalId.trim();
    if (!canonical) {
      toast.error("Enter the canonical dog ID or slug.");
      return;
    }
    if (canonical === recordId) {
      toast.error("Canonical must differ from this dog.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/records/dogs/merge", {
        canonical_id: canonical,
        duplicate_id: recordId,
      });
      const n = r.data?.relinked
        ? Object.values(r.data.relinked).reduce((a, x) => a + (Number(x) || 0), 0)
        : 0;
      toast.success(
        n > 0
          ? `Merged. Re-linked ${n} reference${n === 1 ? "" : "s"}.`
          : "Merged.",
      );
      setMergeOpen(false);
      setCanonicalId("");
      onMerged?.(canonical);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Merge failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mt-10 border border-crimson/30 rounded-xl p-5 bg-[#FFF5F5]"
      data-testid={`admin-tools-${recordType}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <ShieldWarning size={20} weight="duotone" className="text-crimson" />
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-crimson">
          Admin tools · Danger zone
        </p>
      </div>
      <p className="text-[13px] text-[#6B6B6B] leading-relaxed mb-4">
        Cascade-clean references. Financial records (escrow, payments,
        certificates) are always preserved for audit even after delete.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDeleteOpen(true)}
          className="rounded-full border-crimson/50 text-crimson hover:bg-crimson hover:text-white"
          data-testid={`admin-tools-delete-${recordType}`}
        >
          <Trash size={14} weight="duotone" className="mr-1.5" />
          Delete (cascade)
        </Button>
        {recordType === "dog" && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMergeOpen(true)}
            className="rounded-full border-ink/30"
            data-testid="admin-tools-merge-dog"
          >
            <GitMerge size={14} weight="duotone" className="mr-1.5" />
            Merge into another dog
          </Button>
        )}
      </div>

      {/* Cascade-delete confirm dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {recordType}: {recordName}</DialogTitle>
            <DialogDescription>
              This will permanently delete this {recordType} and cascade-clean
              every related reference. Type the {recordType}'s name exactly
              to confirm.
            </DialogDescription>
          </DialogHeader>
          <Label htmlFor="cascade-confirm" className="text-xs font-bold uppercase tracking-[0.15em]">
            Type <code className="text-crimson">{recordName}</code>
          </Label>
          <Input
            id="cascade-confirm"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="mt-1 font-mono"
            placeholder={recordName}
            autoComplete="off"
            data-testid="admin-tools-cascade-confirm"
          />
          {/* Inline hint so admins know whether the typed text matches.
              Helps when the stored record name has subtle whitespace or
              capitalisation that's invisible in the display. */}
          {confirmText.length > 0 && !confirmMatches && (
            <p className="text-xs text-crimson mt-1">
              Doesn't match yet — match is case-insensitive and ignores spaces.
            </p>
          )}
          {confirmMatches && (
            <p className="text-xs text-emerald-700 mt-1">Match confirmed.</p>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setDeleteOpen(false); setConfirmText(""); }}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={onDelete}
              disabled={busy || !confirmMatches}
              className="rounded-full bg-crimson hover:bg-crimson/90 text-white"
              data-testid="admin-tools-cascade-execute"
            >
              {busy ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge-dogs dialog */}
      {recordType === "dog" && (
        <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Merge "{recordName}" into another dog</DialogTitle>
              <DialogDescription>
                This dog is the <strong>duplicate</strong>. Enter the ID or slug
                of the canonical dog to keep. Every parent / offspring / litter
                / pedigree reference will be re-pointed to the canonical
                dog, then this duplicate is deleted.
              </DialogDescription>
            </DialogHeader>
            <Label htmlFor="merge-canonical" className="text-xs font-bold uppercase tracking-[0.15em]">
              Canonical dog ID or slug
            </Label>
            <Input
              id="merge-canonical"
              value={canonicalId}
              onChange={(e) => setCanonicalId(e.target.value)}
              placeholder="e.g. labrador-black-e153 or e153ac2a-19ae-…"
              className="mt-1 font-mono"
              data-testid="admin-tools-merge-canonical"
            />
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => { setMergeOpen(false); setCanonicalId(""); }}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={onMerge}
                disabled={busy || !canonicalId.trim()}
                className="rounded-full bg-ink hover:bg-crimson text-white"
                data-testid="admin-tools-merge-execute"
              >
                {busy ? "Merging…" : "Merge & delete duplicate"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
